import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, MapPin, Crown, Truck } from "lucide-react";
import { api, imageUrl } from "../lib/api";
import { useI18n } from "../i18n";
import { useSeoTitle } from "../context/SeoContext";
import { TrustBar, TrustFooter } from "../components/TrustBadges";
import { HandDeliveryBadge } from "../components/HandDeliveryBadge";
import { HowItWorks } from "../components/HowItWorks";
import { PromoBanner } from "../components/PromoBanner";
import { useCity, catPrefix, productSlug, cityDelay } from "../context/CityContext";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const BANNER = "https://customer-assets.emergentagent.com/job_store-hub-157/artifacts/jvopjj8v_IMG_4469.png";

// Mapping nom → slug identique au backend (city_slug) et aux landings /livraison-cbd/{slug}
const citySlug = (name) => name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const BANNER2 = "https://customer-assets.emergentagent.com/job_store-hub-157/artifacts/6pbhzejt_IMG_4470.png";

const DEFAULT_CITIES = [
  "Paris", "Lille", "Nantes", "Tours", "Lyon", "Bordeaux",
  "Toulouse", "Marseille", "Nice", "Strasbourg", "Mantes-la-Jolie (78)",
  "Évreux", "Rouen", "Cergy-Pontoise", "Les Mureaux",
  "95 Val d’Oise", "Amiens", "Bernay", "Bourgoin-Jallieu", "Bruxelles",
  "Hagondange", "Le Havre", "Liège", "Saint Ouen", "Sarcelles", "Sint-Jans-Molenbeek",
].map((name) => ({ name, vendor_count: 0, live_count: 0 }));

export default function CitySelect() {
  const [cities, setCities] = useState(DEFAULT_CITIES);
  const [q, setQ] = useState("");
  const [addr, setAddr] = useState("");
  const [addrSugs, setAddrSugs] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [catFilter, setCatFilter] = useState("Tout");
  const { city: selectedCity, vendor: cityVendor, delay: cityDelayTxt, setModalOpen, chooseCity } = useCity();
  const [cityProducts, setCityProducts] = useState(null);
  const { addItem } = useCart();
  const navigate = useNavigate();
  const { t } = useI18n();
  useSeoTitle("home", {});

  useEffect(() => {
    api.get("/cities")
      .then((r) => { if (Array.isArray(r.data) && r.data.length) setCities(r.data); })
      .catch(console.error);
    api.get("/catalog/featured")
      .then((r) => { if (Array.isArray(r.data)) setFeatured(r.data); })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!selectedCity || !cityVendor?.id) { setCityProducts(null); return; }
    api.get(`/vendors/${cityVendor.id}/products`)
      .then((r) => {
        const list = (Array.isArray(r.data) ? r.data : []).filter((p) => p.in_stock !== false && p.quantity > 0);
        list.sort((a, b) => (b.best_seller ? 1 : 0) - (a.best_seller ? 1 : 0) || (b.sold_count || 0) - (a.sold_count || 0));
        setCityProducts(list);
      })
      .catch(() => setCityProducts([]));
    setCatFilter("Tout");
  }, [selectedCity, cityVendor?.id]);

  const quickAdd = (p) => {
    if (!selectedCity || !cityVendor) { setModalOpen(true); return; }
    addItem(p.tiers?.length ? { ...p, selectedTier: p.tiers[0] } : p, cityVendor);
    toast.success(`${p.name} ajouté au panier 🛒 (${selectedCity})`);
  };

  const sourceProducts = cityProducts ?? featured;
  const FILTERS = ["Tout", ...Array.from(new Set(sourceProducts.map((p) => p.category).filter(Boolean)))];
  const filteredProducts = sourceProducts.filter((p) => catFilter === "Tout" || p.category === catFilter).slice(0, 4);

  useEffect(() => {
    const query = addr.trim();
    if (query.length < 4) { setAddrSugs([]); return; }
    const timer = setTimeout(() => {
      api.get("/geocode/suggest", { params: { q: query }, timeout: 8000 })
        .then((r) => setAddrSugs(Array.isArray(r.data) ? r.data : []))
        .catch(() => setAddrSugs([]));
    }, 350);
    return () => clearTimeout(timer);
  }, [addr]);

  const norm = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const pickAddr = (s) => {
    setAddrSugs([]);
    setAddr(s.label);
    const found = cities.find((c) => norm(s.label).includes(norm(c.name.replace(/\s*\(.*\)$/, ""))));
    if (found) navigate(`/livraison-cbd/${citySlug(found.name)}`);
    else navigate("/boutique/national");
  };

  const liveCities = cities.filter((c) => c.live_count > 0);

  const filtered = cities.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PromoBanner />
      {/* Hero */}
      <section className="relative min-h-[68vh] flex items-center justify-center overflow-hidden bg-black">
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-neon-green/10 blur-[140px] pointer-events-none" />
        <div className="absolute -bottom-40 -left-20 w-[420px] h-[420px] rounded-full bg-neon-magenta/10 blur-[140px] pointer-events-none" />
        <div className="relative z-10 max-w-5xl mx-auto px-4 w-full flex items-center gap-8">
          <div className="flex-1 text-center md:text-left animate-fade-up">
          <div className="inline-flex items-center gap-2 border border-neon-magenta px-3 py-1 rounded-full mb-6 bg-black/40">
            <Crown className="h-4 w-4 text-neon-magenta" />
            <span className="text-neon-magenta text-xs uppercase tracking-widest font-bold">{t("city.badge")}</span>
          </div>
          <h1 className="font-anton text-5xl sm:text-7xl uppercase text-white leading-none mb-4">
            {t("city.title1")} <span className="neon-text-green">{t("city.title2")}</span>
          </h1>
          <p className="text-zinc-300 text-base sm:text-lg mb-5 max-w-xl mx-auto md:mx-0">
            {t("city.text1")} <span className="text-neon-green font-semibold">{t("city.text1b")}</span>{t("city.text2")}
          </p>
          <HandDeliveryBadge className="mb-6 max-w-xl mx-auto md:mx-0 justify-center md:justify-start" />
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto md:mx-0 mb-3">
            <button
              data-testid="hero-choose-city-btn"
              onClick={() => setModalOpen(true)}
              className="bg-neon-green text-black font-anton uppercase px-6 py-4 glow-green hover:bg-neon-greenDark transition-colors shrink-0"
            >📍 {selectedCity ? selectedCity : "Choisir ma ville"}</button>
          </div>
          <div className="relative max-w-md mx-auto md:mx-0">
            <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-neon-green" />
            <input
              data-testid="hero-address-input"
              value={addr}
              onChange={(e) => setAddr(e.target.value)}
              onBlur={() => setTimeout(() => setAddrSugs([]), 200)}
              placeholder="Ton adresse — on trouve ton livreur…"
              autoComplete="off"
              className="w-full bg-ink-surface/90 border border-neon-green/60 text-white pl-12 pr-4 py-4 focus:outline-none focus:border-neon-green focus:ring-1 focus:ring-neon-green transition-all"
            />
            {addrSugs.length > 0 && (
              <ul data-testid="hero-addr-suggestions" className="absolute z-30 left-0 right-0 top-full bg-ink-surface border border-neon-green/60 shadow-lg max-h-56 overflow-y-auto text-left">
                {addrSugs.map((s, i) => (
                  <li key={s.label}>
                    <button type="button" data-testid="hero-addr-suggestion" onMouseDown={() => pickAddr(s)} className="w-full text-left px-4 py-2.5 text-sm text-zinc-300 hover:bg-neon-green hover:text-black transition-colors border-b border-zinc-800 last:border-b-0">
                      📍 {s.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {liveCities.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4 justify-center md:justify-start" data-testid="hero-live-hubs">
              <span className="text-zinc-500 text-[11px] uppercase font-bold self-center">Express actif :</span>
              {liveCities.slice(0, 6).map((c) => (
                <a key={c.name} href={`/livraison-cbd/${citySlug(c.name)}`} data-testid="hero-hub-chip" className="inline-flex items-center gap-1.5 border border-neon-green/50 bg-neon-green/5 text-neon-green text-xs font-bold px-2.5 py-1 hover:bg-neon-green hover:text-black transition-colors">
                  <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute h-full w-full rounded-full bg-neon-green opacity-75" /><span className="relative rounded-full h-1.5 w-1.5 bg-neon-green" /></span>
                  {c.name} · {cityDelay(c.name) ? `~${cityDelay(c.name)} min` : (c.delivery_time || "< 2h")}
                </a>
              ))}
            </div>
          )}
          <img src="/lion-crossed.webp" alt="Mascotte STUFF" className="md:hidden mx-auto h-52 object-contain mt-6" loading="lazy" />
          </div>
          <img src="/lion-crossed.webp" alt="Mascotte STUFF — lion street" className="hidden md:block w-[420px] lg:w-[540px] object-contain animate-fade-up" loading="eager" />
        </div>
      </section>

      <TrustBar />

      {/* Disponible près de chez toi — meilleures ventes */}
      {sourceProducts.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-14" data-testid="featured-products">
          <p className="text-neon-green text-xs uppercase font-bold tracking-widest mb-1" data-testid="featured-subtitle">
            {cityProducts ? `Stock réel de ${cityVendor?.name || "ta boutique"} · ${cityVendor?.is_national ? "livré en 48h" : `livré en ~${cityDelayTxt || 30} min`}` : "Stock garanti · Prêt à emballer"}
          </p>
          <h2 className="font-anton text-3xl sm:text-4xl uppercase text-white mb-4" data-testid="featured-title">Disponible immédiatement <span className="neon-text-magenta">{cityProducts ? `à ${selectedCity}` : "près de chez toi"}</span></h2>
          <div className="flex flex-wrap gap-2 mb-6" data-testid="featured-filters">
            {FILTERS.map((f) => (
              <button key={f} type="button" data-testid="featured-filter-chip" onClick={() => setCatFilter(f)}
                className={`text-xs uppercase font-bold px-3 py-1.5 border transition-colors ${catFilter === f ? "bg-neon-green text-black border-neon-green" : "border-zinc-700 text-zinc-400 hover:border-neon-green hover:text-neon-green"}`}>{f}</button>
            ))}
          </div>
          {!selectedCity && (
            <button data-testid="featured-locked-note" onClick={() => setModalOpen(true)} className="w-full border border-neon-magenta/60 bg-neon-magenta/5 text-neon-magenta text-sm font-bold px-4 py-3 mb-4 text-left hover:bg-neon-magenta hover:text-white transition-colors">
              🔒 Choisis ta ville pour débloquer les prix locaux et commander →
            </button>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {filteredProducts.map((p) => (
              <div key={p.id} className={`group bg-ink-surface border border-zinc-800 hover:border-neon-green/60 transition-all flex flex-col ${!selectedCity ? "grayscale-[65%] opacity-80" : ""}`} data-testid="featured-product-card">
                <Link to={`/${catPrefix(p.category)}/${productSlug(p.name)}`} data-testid="featured-product-link" className="relative block">
                  <img src={imageUrl(p.image_url) || "/stuff-logo.png"} alt={p.name} className="w-full h-44 object-cover bg-ink" loading="lazy" onError={(e) => { e.target.src = "/stuff-logo.png"; e.target.className = "w-full h-44 object-contain bg-ink p-6"; }} />
                  {p.cbd_rate && <span className="absolute top-2 left-2 bg-black/80 border border-neon-green/60 text-neon-green text-[11px] font-bold px-2 py-0.5">CBD {p.cbd_rate}</span>}
                  {p.tiers?.length > 0 && <span className="absolute top-2 right-2 bg-neon-magenta text-white text-[10px] font-bold uppercase px-2 py-0.5">Tarifs dégressifs</span>}
                  {cityProducts && p.quantity <= 2 && <span data-testid="featured-lowstock" className="absolute bottom-2 left-2 bg-orange-500 text-black text-[10px] font-bold uppercase px-2 py-0.5">⚡ Plus que {p.quantity}</span>}
                </Link>
                <div className="p-4 flex flex-col flex-1">
                  <p className="text-zinc-500 text-[10px] uppercase font-bold tracking-wide">{p.category} <span className="text-zinc-600 normal-case">· THC {p.thc_rate || "< 0,3%"}</span></p>
                  <Link to={`/${catPrefix(p.category)}/${productSlug(p.name)}`} className="font-anton text-lg uppercase text-white leading-tight mt-1 hover:text-neon-green transition-colors">{p.name}</Link>
                  <p className="text-zinc-500 text-xs mt-1 line-clamp-2 flex-1">{p.description}</p>
                  <div className="flex items-center justify-between mt-3 gap-2">
                    <span className="font-anton text-xl text-neon-green">
                      {p.promo && p.original_price ? <span className="text-zinc-500 text-sm line-through mr-1.5">{Number(p.original_price).toFixed(2)}€</span> : null}
                      {p.tiers?.length ? `dès ${Math.min(...p.tiers.map((x) => x.price)).toFixed(2)}€` : `${Number(p.price).toFixed(2)}€`}
                    </span>
                    <button
                      type="button"
                      data-testid="featured-add-cart"
                      onClick={() => quickAdd(p)}
                      className="flex items-center gap-1 bg-neon-green text-black font-anton uppercase text-sm px-3 py-2 hover:bg-neon-magenta hover:text-white transition-colors"
                    >+ Panier</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* City grid */}
      <section id="villes" className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
        <HowItWorks />
        <h2 className="font-anton text-3xl uppercase text-white mb-8 text-center">{t("city.available1")} <span className="neon-text-magenta">{t("city.available2")}</span></h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((c) => (
            <Link
              key={c.name}
              data-testid="city-selector-button"
              to={`/livraison-cbd/${citySlug(c.name)}`}
              className="group relative block bg-ink-surface border border-zinc-800 p-5 text-left hover:border-neon-green hover:shadow-[0_0_15px_rgba(157,255,0,0.2)] transition-all"
            >
              <MapPin className="h-5 w-5 text-neon-magenta mb-3 group-hover:text-neon-green transition-colors" />
              <div className="font-anton text-lg uppercase text-white leading-tight">{c.name}</div>
              <div className="flex items-center gap-2 mt-2 text-xs">
                <span className="text-zinc-500">{c.vendor_count} {c.vendor_count > 1 ? t("city.vendors") : t("city.vendor")}</span>
                {c.live_count > 0 && (
                  <span className="flex items-center gap-1.5 text-neon-green font-bold" data-testid="city-live-count">
                    <span className="relative flex h-2 w-2"><span className="animate-ping absolute h-full w-full rounded-full bg-neon-green opacity-75" /><span className="relative rounded-full h-2 w-2 bg-neon-green" /></span>
                    {c.live_count} {t("city.online")}
                  </span>
                )}
              </div>
              {c.live_count > 0 ? (
                <div className="flex items-center gap-1.5 mt-1.5 text-xs text-neon-green font-bold" data-testid="city-live-badge">
                  ⚡ {t("city.fast")} · {t("city.eta")} {c.delivery_time || "< 2h"}
                </div>
              ) : c.vendor_count > 0 ? (
                <div className="mt-1.5 text-xs text-zinc-600" data-testid="city-closed-badge">🔴 {t("city.closedNow")}</div>
              ) : (
                <div className="mt-1.5 text-xs">
                  <span className="text-zinc-600 block">📦 {t("city.parcel48")}</span>
                  <span
                    data-testid="city-recruit-badge"
                    role="link"
                    tabIndex={0}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate("/recrutement"); }}
                    className="inline-block mt-1 text-neon-magenta font-bold uppercase border border-neon-magenta/50 px-2 py-0.5 hover:bg-neon-magenta hover:text-white transition-colors"
                  >🚀 {t("city.recruit")}</span>
                </div>
              )}
            </Link>
          ))}
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-10">
            <img src="/lion-portrait.webp" alt="Mascotte STUFF" className="mx-auto h-40 object-contain opacity-90 mb-4" loading="lazy" />
            <p className="text-zinc-500">{t("city.none")}</p>
          </div>
        )}

        {/* Bannière boutique nationale 48h */}
        <button
          data-testid="national-shop-banner"
          onClick={() => navigate("/boutique/national")}
          className="group relative w-full mt-10 overflow-hidden border-2 border-neon-magenta bg-ink-surface text-left transition-all hover:shadow-[0_0_30px_rgba(192,38,211,0.45)] hover:border-neon-green"
        >
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(192,38,211,0.18),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(157,255,0,0.14),transparent_55%)]" />
          <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 px-6 sm:px-10 py-7">
            <div>
              <p className="font-graffiti text-neon-magenta text-2xl mb-1">{t("city.natTag")}</p>
              <h3 className="font-anton text-2xl sm:text-4xl uppercase text-white leading-tight">
                {t("city.natTitle1")} <span className="neon-text-green">{t("city.natTitle2")}</span>
              </h3>
              <p className="text-zinc-400 text-sm mt-2">{t("city.natText")}</p>
            </div>
            <span className="shrink-0 inline-flex items-center gap-2 bg-neon-green text-black font-anton uppercase px-6 py-3 glow-green group-hover:bg-neon-magenta group-hover:text-white transition-colors">
              <Truck className="h-5 w-5" /> {t("city.natCta")}
            </span>
          </div>
        </button>
      </section>

      {/* Recrutement — Rejoins le réseau STUFF */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-10" data-testid="recruit-section">
        <div className="relative overflow-hidden border border-zinc-800 bg-ink-surface">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(157,255,0,0.10),transparent_55%)]" />
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8 px-6 sm:px-10 py-10">
            <div>
              <p className="text-neon-green text-xs uppercase font-bold tracking-widest mb-2">Expansion STUFF CBD</p>
              <h2 className="font-anton text-3xl sm:text-4xl uppercase text-white leading-tight">Fais tourner la ville. <span className="neon-text-green">Rejoins le réseau STUFF.</span></h2>
              <p className="text-zinc-400 text-sm mt-3 max-w-lg">Que tu sois un livreur urbain cherchant des créneaux flexibles et bien rémunérés, ou un shop CBD indépendant qui veut multiplier ses ventes en livraison express sans tracas logistique.</p>
              <div className="flex flex-wrap gap-3 mt-6">
                <Link to="/recrutement" data-testid="recruit-courier-btn" className="inline-flex items-center gap-2 bg-neon-green text-black font-anton uppercase px-5 py-3 glow-green">🛵 Devenir livreur express</Link>
                <Link to="/register" data-testid="recruit-shop-btn" className="inline-flex items-center gap-2 border border-neon-magenta text-neon-magenta font-anton uppercase px-5 py-3 hover:bg-neon-magenta hover:text-white transition-colors">🏪 Référencer mon shop</Link>
                <Link to="/club" data-testid="home-club-btn" className="inline-flex items-center gap-2 border border-neon-green text-neon-green font-anton uppercase px-5 py-3 hover:bg-neon-green hover:text-black transition-colors">👑 Club STUFF</Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 self-center">
              {[["💶", "Paiements hebdo", "Bilan et facture chaque semaine, gains transparents."], ["0€", "Frais d'entrée", "Aucun coût pour rejoindre le réseau."], ["📦", "Stock fourni", "Dotation initiale de produits pour démarrer."], ["🛡️", "100% légal", "Produits certifiés THC < 0,3%, cadre encadré."]].map(([icon, title, text]) => (
                <div key={title} className="bg-ink border border-zinc-800 p-4">
                  <p className="font-anton text-xl text-neon-green">{icon}</p>
                  <p className="text-white text-sm font-bold uppercase mt-1">{title}</p>
                  <p className="text-zinc-500 text-xs mt-0.5">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Banner */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 mb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative overflow-hidden border border-zinc-800">
            <img src={BANNER} alt="STUFF CBD" className="w-full h-56 sm:h-72 object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/40 to-transparent flex items-center">
              <div className="px-6 sm:px-12">
                <p className="font-graffiti text-neon-green text-2xl mb-1">Goût de fou</p>
                <h3 className="font-anton text-3xl sm:text-4xl uppercase text-white">Puissance maximale</h3>
              </div>
            </div>
          </div>
          <div className="relative overflow-hidden border border-zinc-800">
            <img src={BANNER2} alt="STUFF Pre-rolls" className="w-full h-56 sm:h-72 object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/40 to-transparent flex items-center">
              <div className="px-6 sm:px-12">
                <p className="font-graffiti text-neon-magenta text-2xl mb-1">Plus. Relax. Repeat.</p>
                <h3 className="font-anton text-3xl sm:text-4xl uppercase text-white">Pre-rolls premium</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      <TrustFooter />
    </div>
  );
}
